import { rankItem, } from '@tanstack/match-sorter-utils';
import { ColumnDef, FilterFn, Row, SortingFn } from '@tanstack/react-table';
import { clsx, type ClassValue } from 'clsx';
import _ from 'lodash';
import { twMerge } from 'tailwind-merge';
import { download, generateCsv, mkConfig } from 'export-to-csv';
import moment from 'moment';
import { assoc, assocPath, curry, has, head, ifElse, isEmpty, isNil, last, map, mergeAll, path, prop, reduce } from 'ramda';
import { z } from 'zod';
import { COUNTRY_PREFIXES, ORDER_TYPES } from './constants';
import { statusListSchema } from './types';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export const dollarUSLocale = Intl.NumberFormat('en-US');

export function splitString(string: string) {
  const camelSplit = string.split(/(?=[A-Z])/); // split by camel case
  const snakeSplit = string.split('_'); // split by snake case
  return (camelSplit.length > snakeSplit.length ? camelSplit : snakeSplit).join(' ');
}

export const cleanEntries = (obj: any, filter: (v: any) => boolean = (v) => v) => Object.fromEntries(Object.entries(obj).filter(([_, v]) => filter(v)));

export const getCountryCode = (company: any) => {
  const country = COUNTRY_PREFIXES.find((country) =>
    country.name == company.country
  );
  return country ? country.value : '52';
}

export const whatsappUrl = (company: any, text = '') => {
  const countryCode = getCountryCode(company);
  const phone = company.cellphone?.replace(/\D/g, '');
  return `https://wa.me/${countryCode}${phone}?text=${encodeURIComponent(text)}`;
}

export const stripeUrl = (company: any, text = '') => `https://dashboard.stripe.com/customers/${company.stripe_customer_id}`;

export const importContactText = (company: any, text = '') => {
  const countryCode = getCountryCode(company);
  const phone = company.cellphone?.replace(/\D/g, '');
  return `${company.id} ${company.name},+${countryCode}${phone},${company.email}`;
}

export const fuzzyFilter: FilterFn<any> = (row, columnId, value, addMeta) => {
  const itemRank = rankItem(row.getValue(columnId), value);
  addMeta({ itemRank, });
  return itemRank.passed;
}

export const translateStatus = (statusList: z.infer<typeof statusListSchema>) => {
  const statusNameMap = {
    'ServiceStatus.RECEIVED': 'Recibido',
    'ServiceStatus.AUTHORIZED': 'Autorizado',
    'ServiceStatus.IN_PROGRESS': 'En Proceso',
    'ServiceStatus.FINISHED': 'Terminado',
    'ServiceStatus.DELIVERED': 'Entregado',
    'ServiceStatus.CANCELLED': 'Cancelado',
  };

  const translated = statusList.map(status => {
    return {
      ...status,
      description: statusNameMap[status.description] || status.description,
    }
  });

  return _.sortBy(translated, ['order']);
}

interface DateFilterValue {
  gte?: string;
  lte?: string;
}

export const dateBetweenFilter: FilterFn<any> = (row, columnId, filterValue, addMeta) => {
  if (!filterValue) return true;

  // Convertir el valor de la celda a timestamp de Unix
  const cellValue = row.getValue(columnId);
  if (!cellValue) return false;
  
  const dateValue = moment(cellValue).unix();
  
  // Para el nuevo formato de objeto con gte y lte
  if (typeof filterValue === 'object' && (filterValue.gte || filterValue.lte)) {
    const filterObj = filterValue as DateFilterValue;
    const start = filterObj.gte ? moment(filterObj.gte).unix() : 0;
    const end = filterObj.lte ? moment(filterObj.lte).unix() : Number.MAX_SAFE_INTEGER;
    
    return start <= dateValue && dateValue <= end;
  }
  
  // Compatibilidad con el formato anterior de array [start, end]
  if (Array.isArray(filterValue)) {
    const [start = 0, end = moment().unix()] = filterValue;
    const startUnix = typeof start === 'string' ? moment(start).unix() : start;
    const endUnix = typeof end === 'string' ? moment(end).unix() : end;
    
    return startUnix <= dateValue && dateValue <= endUnix;
  }
  
  return true;
}

export const booleanFilter: FilterFn<any> = (row, columnId, filterValue, addMeta) => {
  if (!filterValue) return true;
  const cellValue = row.getValue(columnId);
  if (!cellValue) return false;
  return cellValue === filterValue;
}

export const numberFilter: FilterFn<any> = (row, columnId, filterValue, addMeta) => {
  if (!filterValue && filterValue !== 0) return true;
  const cellValue = row.getValue(columnId);
  if (cellValue === null || cellValue === undefined) return false;
  return Number(cellValue) === Number(filterValue);
}

export const inNumberRange: FilterFn<any> = (row, columnId, filterValue, addMeta) => {
  if (!filterValue || !Array.isArray(filterValue)) return true;
  
  const cellValue = row.getValue(columnId);
  if (cellValue === null || cellValue === undefined) return false;
  
  const numValue = Number(cellValue);
  if (isNaN(numValue)) return false;
  
  const [min, max] = filterValue;
  
  if (min !== undefined && min !== null && min !== '' && numValue < Number(min)) {
    return false;
  }
  
  if (max !== undefined && max !== null && max !== '' && numValue > Number(max)) {
    return false;
  }
  
  return true;
}

// const dateA = new Date(dateAStr); // validate, then  // const timeA = dateA.getTime();
export const dateSortFn = (rowA: any, rowB: any, columnId: any, propsPath: string[]): number => {
  if (!rowA.getValue(columnId)?.caUnix || !rowB.getValue(columnId)?.caUnix) {
    return 0;
  }

  const dateA = path(propsPath, rowA.getValue(columnId));
  const dateB = path(propsPath, rowB.getValue(columnId));

  if (!dateA || !dateB) {
    return 0;
  }

  if (dateA < dateB) {
    return -1;
  } else if (dateA > dateB) {
    return 1;
  } else {
    return 0;
  }
}

export const truncateString = (str, num) => {
  if (str.length <= num) {
    return str;
  }
  return str.slice(0, num) + '...';
}

export const minutesToHours = (minutes: number) => {
  if (!minutes) return `0 minutos`;
  return `${minutes} hrs`;
}

export const getPercentage = (numerator: number, divider: number) => {
  if (divider == 0) return `0 %`;
  const result = +(numerator / divider) * 100;
  return ` ${result.toFixed(2)} %`;
}

// Horas tabuladas de una mano de obra: son las horas guardadas en
// unitCostMultiplier. NO se multiplica por quantity (numero de manos de obra).
export const getMasterTabulatedHours = (item: any): number =>
  Number(item?.unitCostMultiplier) || 0;

export const exportToCsv = ({ rows, columns, title }) => {
  // console.log(rows)
  // console.log(columns)
  const csvConfig = mkConfig({
    fieldSeparator: ',',
    filename: title,
    title,
    decimalSeparator: '.',
    useKeysAsHeaders: false, //columns && columns.length == 0,
    showColumnHeaders: true,
    columnHeaders: columns
  });
  const csv = generateCsv(csvConfig)(rows);
  download(csvConfig)(csv);
}

export const transformData = (columns: ColumnDef<any>[], data) => {
  const getHeader = ifElse(
    has('exportHeaderLabel'),
    prop('exportHeaderLabel'),
    prop('header') as any,
  );

  const getValue = row => col => {
    return col.exportCell ? col.exportCell({ original: row }) : col.accessorFn(row);
  };

  const transformRow = row => {
    return reduce((acc, col) => {
      const header = getHeader(col);
      const value = getValue(row)(col);
      return assoc(header as any, value, acc);
    }, {}, columns);
  };

  return map(transformRow, data);
};

// these three fns are used to transform react table 'columnFilters' to prisma where and orderBy
const dotNotationToNestedObject = curry((path: string, value: string) => {
  return assocPath(path.split('.'), value, {});
});

export const getOrderByClause = (sorting?: Array<{ id: string, desc: boolean }>) => {
  if (isNil(sorting) || isEmpty(sorting)) return undefined;

  const { id, desc } = head(sorting)!;
  const value = desc ? 'desc' : 'asc';
  return dotNotationToNestedObject(id, value);
};

export const getWhereClause = (filters?: Array<{ id: string, value: any }>) => {
  if (isNil(filters) || isEmpty(filters)) return undefined;

  const transformFilter = ({ id, value }: { id: string, value: any }) => {
    const key = last(id.split('.'));
    const transformedValue = ['name', 'description'].includes(key!)
      ? { contains: value }
      : value;
    return dotNotationToNestedObject(id, transformedValue);
  };

  // Combine all filter transformations
  return mergeAll(map(transformFilter, filters));
};

// transforms to {entry.header: entry.counter}
export function transformToKeyValue(input: any) {
  return input.map(({ name, data }) =>
    data.reduce((acc, { header, counter, sum, average }) => {
      acc[header] = { counter, sum, average };
      return acc;
    }, { name })
  );
}

export function formatAppointmentDate(date: Date) {
  return moment(date).format('DD/MM/YYYY HH:mm');
}

export function overrideOrderTypes(BUSINESS_CONFIG) {
  const businessLogicData = BUSINESS_CONFIG.data;
  const overrideOrderType = businessLogicData?.services?.overrideOrderTypes;

  const orderTypesOverride = {
    1: ORDER_TYPES[1],
    2: ORDER_TYPES[2],
    6: ORDER_TYPES[6],
  };

  let orderTypes: any = ORDER_TYPES;
  if (typeof overrideOrderType === 'boolean') {
    orderTypes = overrideOrderType ? orderTypesOverride : orderTypes;
  }

  if (typeof overrideOrderType === 'object') {
    orderTypes = overrideOrderType.override ? orderTypesOverride : orderTypes;

    if (overrideOrderType.replace) {
      for (const key in overrideOrderType.replace) {
        if (Number(key) in orderTypes) {
          orderTypes[Number(key)] = overrideOrderType.replace[key];
        }
      }
    }

    if (Array.isArray(overrideOrderType.hide)) {
      for (const num of overrideOrderType.hide) {
        if (num in orderTypes) {
          delete orderTypes[num];
        }
      }
    }

    if (overrideOrderType.add && typeof overrideOrderType.add === 'object') {
      for (const key in overrideOrderType.add) {
        orderTypes[Number(key)] = overrideOrderType.add[key];
      }
    }

  }
  return orderTypes;
}